import { useEffect, useState, type MouseEvent } from 'react'

export interface LightboxItem {
  id: string
  src: string
  label: string
  downloadHref: string
}

interface LightboxProps {
  items: LightboxItem[]
  index: number
  onIndexChange: (next: number) => void
  onClose: () => void
}

export default function Lightbox({ items, index, onIndexChange, onClose }: LightboxProps) {
  const current = items[index]
  const [downloading, setDownloading] = useState(false)
  const [downloadFailed, setDownloadFailed] = useState(false)

  // A stale failure notice from the previous photo would be read as belonging
  // to the one now on screen.
  useEffect(() => { setDownloadFailed(false) }, [index])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!current) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') onIndexChange((index + 1) % items.length)
      if (e.key === 'ArrowLeft') onIndexChange((index - 1 + items.length) % items.length)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, items.length, onIndexChange, onClose, current])

  if (!current) return null

  /**
   * The `download` attribute is honoured only for same-origin, `blob:` and
   * `data:` URLs. `downloadHref` points at the API origin — never the page's
   * origin in dev or prod — so the browser silently ignores it and follows the
   * link instead, replacing the album with the raw image (the API serves
   * variants `Content-Disposition: inline`). So fetch the bytes ourselves and
   * hand the browser a same-origin `blob:` URL, which it will honour, carrying
   * the photo's real label as the filename. The production CSP already allows
   * `connect-src` to the API origin.
   *
   * The href is kept on the anchor so "open in new tab" and "save link as"
   * still work, and so the control stays a real, keyboard-focusable link.
   */
  const handleDownload = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (downloading) return
    setDownloading(true)
    setDownloadFailed(false)
    let objectUrl: string | null = null
    try {
      const response = await fetch(current.downloadHref)
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`)
      objectUrl = URL.createObjectURL(await response.blob())
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = current.label
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } catch {
      setDownloadFailed(true)
    } finally {
      // Revoke on the next tick, not synchronously: some browsers have not yet
      // picked up the blob when the click handler returns.
      if (objectUrl) {
        const url = objectUrl
        setTimeout(() => URL.revokeObjectURL(url), 0)
      }
      setDownloading(false)
    }
  }

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={current.label}>
      <button className="lightbox__close" onClick={onClose} aria-label="Close">×</button>
      <button
        className="lightbox__nav lightbox__nav--prev"
        onClick={() => onIndexChange((index - 1 + items.length) % items.length)}
        aria-label="Previous"
      >‹</button>
      <img className="lightbox__image" src={current.src} alt={current.label} />
      <button
        className="lightbox__nav lightbox__nav--next"
        onClick={() => onIndexChange((index + 1) % items.length)}
        aria-label="Next"
      >›</button>
      <a
        className="lightbox__download"
        href={current.downloadHref}
        download={current.label}
        onClick={handleDownload}
        aria-busy={downloading}
      >
        {downloading ? 'Downloading…' : 'Download'}
      </a>
      {downloadFailed && (
        <p className="lightbox__error" role="alert">
          Download failed. Please try again.
        </p>
      )}
    </div>
  )
}
